import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  emailValidators,
  firstNameValidators,
  lastNameValidators,
  usernameValidators
} from '../../../../utils/const/profile';
import { uniqueField } from '../../../../utils/validators/unique-field.validator';
import { UserStoreService } from '../../../../shared/services/state-management/user-store.service';
import {
  AccountType,
  AuthenticationService,
  RegisterUser,
  UserService,
  UserRegisterFrom,
  Kaza,
  User, filterUndefined,
} from '@adeo/ngx-kozikaza-api';
import { catchError, switchMap } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AuthService } from '../../auth.service';
import { KazaService as LegacyKazaService } from '../../../../shared/services/features/kaza/kaza.service';
import { of } from 'rxjs/internal/observable/of';
import { PhoneCode } from '../../../../shared/models/auth/phone-codes';
import { HydraErrorFull } from '../../../../utils/forms/form-utilities';

@Injectable({
  providedIn: 'root'
})
export class SignupFormLiteService {
  signupForm_: UntypedFormGroup;
  usernameControl: UntypedFormControl;
  plainPasswordControl: UntypedFormControl;
  user: User;
  codeSelected: PhoneCode;
  tmpCodeSelected: number;
  error: HydraErrorFull;

  constructor(
    private fb: UntypedFormBuilder,
    public userStoreService: UserStoreService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private authService: AuthService,
    private legacyKazaService: LegacyKazaService,
  ){}

  initForm() {
    this.signupForm_ = this.fb.group({
      firstName: ['', [...firstNameValidators]],
      lastName: ['', [...lastNameValidators]],
      email: ['', [
        ...emailValidators],
        uniqueField.bind(this, 'email', null),
      ],
      phoneNumber: ['', [
        Validators.required,
        Validators.pattern(/^[0-9]{7,9}$/),
        Validators.maxLength(9)
      ]],
      cgu: [false, [Validators.requiredTrue]],
    });

    this.usernameControl = new UntypedFormControl(
      '',
      [...usernameValidators],
      uniqueField.bind(this, 'username', null),
    );

    this.plainPasswordControl = new UntypedFormControl(
      '',
      [Validators.required, Validators.minLength(7)],
    );

    this.userStoreService.userStore.pipe(
      filterUndefined()
    ).subscribe(
      (u: User) => {
        this.user = u;
        if (!!this.user) {
          this.signupForm_.removeControl('emailConfirmation');
          this.signupForm_.removeControl('plainPassword');
          this.signupForm_.removeControl('country');
          this.signupForm_.removeControl('postalCode');
          this.signupForm_.removeControl('username');
          this.signupForm_.get('email').clearAsyncValidators();
          this.signupForm_.get('email').updateValueAndValidity();

          this.tmpCodeSelected = (this.user.phoneNumber && this.user.phoneNumber.countryCode) ? this.user.phoneNumber.countryCode : null;
          this.signupForm_.patchValue({
            firstName: ((this.user.firstName) ? this.user.firstName : ''),
            lastName: ((this.user.lastName) ? this.user.lastName : ''),
            email: ((this.user.email) ? this.user.email : ''),
            phoneNumber: ((this.user.phoneNumber && this.user.phoneNumber.number) ? this.user.phoneNumber.number : ''),
          });
        } else {
          this.signupForm_ = this.fb.group({
            ...this.signupForm_.controls,
            emailConfirmation: [
              '',
              [
                Validators.required,
                Validators.pattern(/^.+\@\S+\.\S+$/),
                (control: UntypedFormControl) => {
                  if (control.parent && control.parent.controls && control.parent.controls['email']) {
                    if (control.parent.controls['email'].value !== control.value) {
                      return ({mismatch: true});
                    }
                  }
                  return null;
                },
              ],
            ],
            plainPassword: this.plainPasswordControl,
            country: ['FR', [Validators.required]],
            postalCode: ['', [Validators.required, this.postalCodeValidator]],
            username: this.usernameControl,
            optInPartnerEmail: [false]
          });

          this.countryOnChanges();
          this.emailOnChanges();
        }
      }
    );
  }

  countryOnChanges(): void {
    this.signupForm_.get('country').valueChanges.subscribe(
      data => {
        if (data !== 'FR') {
          this.signupForm_.get('postalCode').setValidators(null);
        } else {
          this.signupForm_.get('postalCode').setValidators([Validators.required, this.postalCodeValidator]);
        }

        this.signupForm_.get('postalCode').updateValueAndValidity();
      });
  }

  emailOnChanges(): void {
    this.signupForm.get('email').valueChanges.subscribe(() => {
      if (this.signupForm.controls.hasOwnProperty('emailConfirmation')) {
        this.signupForm.controls['emailConfirmation'].updateValueAndValidity();
      }
    });
  }

  postalCodeValidator(c: UntypedFormControl) {
    const validPostalCode = /^[0-9]{5}$/i;
    const group = c.parent;
    let country: string;

    if (group) {
      country = group.get('country').value;

      if (('FR' !== country && '' !== c.value) || ('FR' === country && validPostalCode.test(c.value))) {
        return null;
      }
    }

    return {postalCodeValidError: {value: true}};
  }

  set signupForm(form: UntypedFormGroup) {
    this.signupForm_ = this.signupForm;
  }

  get signupForm(): UntypedFormGroup {
    return this.signupForm_;
  }

  save(): Observable<any> {
    this.signupForm.markAllAsTouched();
    if (this.signupForm.valid && !this.user) {
      const userFields = this.getUserFields();
      const kazaFields = this.getKazaFields();

      return this.authenticationService.signup(userFields).pipe(
        switchMap((data) =>
          this.legacyKazaService.update((typeof data.user.activeKaza == 'string') ? data.user.activeKaza: data.user.activeKaza['@id'], kazaFields)
        ),
        catchError((err) => {
          this.error = err.error;
          return throwError(err);
        })
      )
    } else {
      return of(null);
    }
  }

  getUserFields(): Partial<RegisterUser> {
    const {
      firstName,
      lastName,
      email,
      username,
      plainPassword,
      optInPartnerEmail
    }: Partial<RegisterUser> = this.signupForm.value;

    return {
      firstName,
      lastName,
      email,
      username,
      plainPassword,
      optInPartnerEmail,
      accountType: AccountType.Particular,
      registerFrom: UserRegisterFrom.Coaching
    };
  }

  getKazaFields(): Partial<Kaza> {
    return {
      country: this.signupForm_.get('country').value,
      postalCode: this.signupForm_.get('postalCode').value ? this.signupForm_.get('postalCode').value.toString() : '',
      types: ['/project_types/6bac1cb7-a797-40ab-9f42-1e80f82164e6'] // no-work
    }
  }
}
